import { useEffect, useState, type ReactNode } from 'react';
import { Field } from '@/shared/ui/Field';
import { dataLocalDeIso, formatarDataIso, isoDataLocal } from '@/shared/format/datas';

/**
 * Card "Filtros" dos relatórios: período (início/fim) com atalhos e, logo
 * abaixo das datas, os filtros extras da aba (`children`).
 *
 * No celular (≤540px) o card vira um `<details>` recolhível cujo resumo
 * mostra "Período dd/mm–dd/mm · N filtros" — assim a lista do relatório
 * aparece sem precisar rolar por cima de todos os filtros.
 */

export type AtalhoPeriodo = 'hoje' | '7d' | 'mes' | 'mesPassado' | '30d' | 'ano';

export const ATALHOS_PERIODO: Array<{ id: AtalhoPeriodo; rotulo: string }> = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: '7d', rotulo: 'Últimos 7 dias' },
  { id: 'mes', rotulo: 'Este mês' },
  { id: 'mesPassado', rotulo: 'Mês passado' },
  { id: '30d', rotulo: 'Últimos 30 dias' },
  { id: 'ano', rotulo: 'Este ano' },
];

/** Intervalo (YYYY-MM-DD, dia local) de um atalho, relativo a `hoje`. */
export function periodoDoAtalho(
  atalho: AtalhoPeriodo,
  hoje: Date = new Date(),
): { inicio: string; fim: string } {
  const a = hoje.getFullYear();
  const m = hoje.getMonth();
  const d = hoje.getDate();
  const fimHoje = isoDataLocal(new Date(a, m, d));
  switch (atalho) {
    case 'hoje':
      return { inicio: fimHoje, fim: fimHoje };
    case '7d':
      // 7 dias contando hoje.
      return { inicio: isoDataLocal(new Date(a, m, d - 6)), fim: fimHoje };
    case '30d':
      // 30 dias contando hoje.
      return { inicio: isoDataLocal(new Date(a, m, d - 29)), fim: fimHoje };
    case 'mes':
      return { inicio: isoDataLocal(new Date(a, m, 1)), fim: fimHoje };
    case 'mesPassado':
      return {
        inicio: isoDataLocal(new Date(a, m - 1, 1)),
        // Dia 0 do mês atual = último dia do mês passado.
        fim: isoDataLocal(new Date(a, m, 0)),
      };
    case 'ano':
      return { inicio: isoDataLocal(new Date(a, 0, 1)), fim: fimHoje };
  }
}

/** Atalhos cujo intervalo coincide exatamente com o período informado. */
export function atalhosAtivos(inicio: string, fim: string, hoje: Date = new Date()): AtalhoPeriodo[] {
  return ATALHOS_PERIODO.map((a) => a.id).filter((id) => {
    const p = periodoDoAtalho(id, hoje);
    return p.inicio === inicio && p.fim === fim;
  });
}

/** "Período 01/09–30/09 · 2 filtros" — resumo do card recolhido. */
export function resumoPeriodo(inicio: string, fim: string, quantidadeFiltros = 0): string {
  const curta = (iso: string) => formatarDataIso(iso).slice(0, 5) || '…';
  const anoInicio = dataLocalDeIso(inicio)?.getFullYear();
  const anoFim = dataLocalDeIso(fim)?.getFullYear();
  // Com anos diferentes o dd/mm sozinho é ambíguo: mostra a data inteira.
  const fmt = anoInicio !== anoFim ? (iso: string) => formatarDataIso(iso) || '…' : curta;
  let texto = `Período ${fmt(inicio)}–${fmt(fim)}`;
  if (quantidadeFiltros > 0) {
    texto += ` · ${quantidadeFiltros} ${quantidadeFiltros === 1 ? 'filtro' : 'filtros'}`;
  }
  return texto;
}

const CONSULTA_CELULAR = '(max-width: 540px)';

function useEhCelular(): boolean {
  const consultar = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(CONSULTA_CELULAR).matches
      : false;
  const [ehCelular, setEhCelular] = useState(consultar);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(CONSULTA_CELULAR);
    const aoMudar = () => setEhCelular(mql.matches);
    aoMudar();
    mql.addEventListener?.('change', aoMudar);
    return () => mql.removeEventListener?.('change', aoMudar);
  }, []);
  return ehCelular;
}

type Props = {
  inicio: string;
  fim: string;
  onPeriodo: (inicio: string, fim: string) => void;
  /** Quantos filtros extras (além do período) estão ativos — vai no resumo do celular. */
  quantidadeFiltros?: number;
  /** Filtros extras da aba, renderizados no mesmo grid das datas. */
  children?: ReactNode;
  titulo?: string;
};

export function FiltrosPeriodo({
  inicio,
  fim,
  onPeriodo,
  quantidadeFiltros = 0,
  children,
  titulo = 'Filtros',
}: Props) {
  const ehCelular = useEhCelular();
  const ativos = atalhosAtivos(inicio, fim);

  const atalhos = (
    <div className="filtros-relatorio__atalhos" role="group" aria-label="Atalhos de período">
      {ATALHOS_PERIODO.map((a) => {
        const ativo = ativos.includes(a.id);
        return (
          <button
            key={a.id}
            type="button"
            className={`btn filtros-relatorio__atalho${ativo ? ' filtros-relatorio__atalho--ativo' : ''}`}
            aria-pressed={ativo}
            onClick={() => {
              const p = periodoDoAtalho(a.id);
              onPeriodo(p.inicio, p.fim);
            }}
          >
            {a.rotulo}
          </button>
        );
      })}
    </div>
  );

  const campos = (
    <div className="filtros-relatorio-grid">
      <Field rotulo="Data início">
        {(id) => (
          <input
            id={id}
            className="input"
            type="date"
            value={inicio}
            onChange={(e) => onPeriodo(e.target.value, fim)}
            max={fim || undefined}
          />
        )}
      </Field>
      <Field rotulo="Data fim">
        {(id) => (
          <input
            id={id}
            className="input"
            type="date"
            value={fim}
            onChange={(e) => onPeriodo(inicio, e.target.value)}
            min={inicio || undefined}
          />
        )}
      </Field>
      {children}
    </div>
  );

  if (ehCelular) {
    return (
      <details className="card filtros-relatorio filtros-relatorio--recolhivel">
        <summary className="filtros-relatorio__resumo">
          <span className="card__title">{titulo}</span>
          <span className="filtros-relatorio__resumo-texto">
            {resumoPeriodo(inicio, fim, quantidadeFiltros)}
          </span>
        </summary>
        <div className="card__body">
          {atalhos}
          {campos}
        </div>
      </details>
    );
  }

  return (
    <div className="card filtros-relatorio">
      <div className="card__header">
        <span className="card__title">{titulo}</span>
        {atalhos}
      </div>
      <div className="card__body">{campos}</div>
    </div>
  );
}
